import { InsuranceTier } from '@brenger/api-client';
import { useModalState } from '@brenger/react';
import cn from 'classnames';
import React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import { useDispatch } from 'react-redux';
import { Button, Dialog, InputSelect, Modal, SimplePrice } from '../../../../../brenger-shared-ui';
import { useTypedSelector } from '../../../../../hooks';
import { translate } from '../../../../../utils/localization';
import { priceAsString } from '../../../../../utils/price';
import { actions } from '../../../ducks';
import iconInfo from './icon-info-circle-green.svg';
import iconShieldGreen from './icon-shield-green.svg';
import iconShield from './icon-shield.svg';
import { TermsBlock } from './TermsBlock';

export const TermsBlockInsurance = (): JSX.Element | null => {
  const modal = useModalState();
  const dispatch = useDispatch();
  const selected = useTypedSelector(state => state.generalTransport.choices?.insurance_tier) || '';
  const advisory = useTypedSelector(state => state.generalTransport.quote)?.service_advisory?.insurance;
  const isLoading = useTypedSelector(state => state.generalTransport.price.isLoading);
  const isEditable = (advisory?.upsell_options?.length || 0) > 0;

  const selectedCoverage =
    selected === null || selected === ''
      ? advisory?.base_coverage
      : advisory?.upsell_options.find(o => o.tier === selected)?.coverage;

  /**
   * When this component mounts (and pricing is done), we check if we need to reset the insurance choice.
   * This is not needed for pricing, it will ignore, but we don't want TRs with extra coverage selected, because of cache
   */
  React.useEffect(() => {
    if (!isEditable && !isLoading && selected) {
      dispatch(actions.setChoice({ insurance_tier: null }));
      dispatch(actions.getPrice());
    }
  }, [isEditable, isLoading]);

  /**
   * Showing insurance:
   * If no base coverage is returned by pricing, we don't show the insurance block.
   * When available:
   * - We show a white banner when no upsells are available
   * - We show a green banner with selectable coverage when upsells are available
   */
  if (!selectedCoverage || selectedCoverage === 0) return null;

  return (
    <div
      style={{
        borderRadius: 8,
        overflow: 'hidden',
        marginTop: 16,
        ...(isEditable
          ? {
              color: '#005A62', // green-700
              border: '1px solid #005A62',
            }
          : {}),
      }}
    >
      <TermsBlock
        borderRadius="top"
        border="bottom"
        icon={
          isEditable ? (
            <img style={{ margin: '.1em 0.5em 0px 0px' }} src={iconShieldGreen} alt="Shield" />
          ) : (
            <img style={{ margin: '.1em 0.5em 0px 0px' }} src={iconShield} alt="Shield" />
          )
        }
        title={
          <Translate
            id="insurance.title"
            data={{
              coverage: priceAsString({
                price: {
                  amount: selectedCoverage,
                  currency: 'EUR',
                },
                addPlusMinusIndicator: false,
              }),
            }}
          />
        }
        className={cn({ '!bg-green-100': isEditable })}
      />
      <TermsBlock className={cn({ '!bg-green-100': isEditable })}>
        <p style={{ marginBottom: isEditable ? 16 : 0 }}>
          <Translate id="insurance.description" />
        </p>
        {isEditable && (
          <>
            <div className="flex items-center gap-1">
              <b>
                <Translate id="insurance.upsell.title" />
              </b>
              <button
                style={{
                  appearance: 'none',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                }}
                onClick={modal.open}
              >
                <img
                  src={iconInfo}
                  alt="Info"
                  style={{
                    width: 16,
                    height: 16,
                  }}
                />
              </button>
            </div>
            <div style={{ marginTop: 8, borderRadius: 8, overflow: 'hidden', backgroundColor: '#fff' }}>
              <InputSelect
                value={selected}
                onChange={v => {
                  dispatch(actions.setChoice({ insurance_tier: (v || null) as InsuranceTier }));
                  dispatch(actions.getPrice());
                }}
                options={[
                  {
                    label: (
                      <Translate
                        id="insurance.upsell.options.default"
                        data={{
                          coverage: priceAsString({
                            price: {
                              amount: advisory?.base_coverage || 0,
                              currency: 'EUR',
                            },
                            addPlusMinusIndicator: false,
                          }),
                        }}
                      />
                    ),
                    value: '',
                  },
                  ...(advisory?.upsell_options || []).map(({ tier, premium, coverage }) => ({
                    label: (
                      <Translate
                        id="insurance.upsell.options.extra"
                        data={{
                          premium: <SimplePrice price={premium} />,
                          coverage: priceAsString({
                            price: {
                              amount: coverage,
                              currency: 'EUR',
                            },
                            addPlusMinusIndicator: false,
                          }),
                        }}
                      />
                    ),
                    value: tier || '',
                  })),
                ]}
                addEmptyOption={false}
              />
            </div>
          </>
        )}
      </TermsBlock>
      <Dialog isActive={modal.isOpen}>
        <Modal open={modal.isOpen} onClick={modal.close} header={<Translate id="insurance.upsell.modal.title" />}>
          <div
            // eslint-disable-next-line @typescript-eslint/naming-convention
            dangerouslySetInnerHTML={{ __html: translate('insurance.upsell.modal.body') }}
            style={{
              marginBottom: 24,
            }}
          />
          <Button buttonStyle="primary" fullWidth={true} onClick={modal.close}>
            <Translate id="insurance.upsell.modal.button" />
          </Button>
        </Modal>
      </Dialog>
    </div>
  );
};
